import { ChangeEvent, JSX, useState} from 'react';
import './styles/single-charge-container.scss';
import {
    useRecordContext,
    useNotify,
    useGetOne
} from 'react-admin';
import {
    Button,
    CircularProgress
} from '@mui/material';
import { SingleChargeCheckoutDrawer } from "./components/single-charge/SingleChargeCheckoutDrawer";
import { currency as currencies, convertCurrencyToSymbol } from "../../../../defaults/defaults";
import { CustomerAccount } from "../../../../types/entities";
import { useASettingByCode } from "../../../global-hooks/default-system-hooks";

function findMonth(num: number): string {
    const months = {
        1: 'Jan',
        2: 'Feb',
        3: 'Mar',
        4: 'Apr',
        5: 'May',
        6: 'Jun',
        7: 'Jul',
        8: 'Aug',
        9: 'Sep',
        10: 'Oct',
        11: 'Nov',
        12: 'Dec'
    };
    return months[num] ?? 'N/A';
}

export const SingleChargeContainer: () => JSX.Element = (): JSX.Element => {
    const [name, setName] = useState<string>('');
    const [quantity, setQuantity] = useState<number>(1);
    const [currency, setCurrency] = useState<string>('gbp');
    const [price, setPrice] = useState<number>(0);
    const [toggleCheckoutPanel, setToggleCheckoutPanel] = useState<boolean>();
    const notify: any = useNotify();
    const { loadingSetting, settingValue, loadSettingValue, error } = useASettingByCode("default_noknok_invoice_html_template");
     const record: CustomerAccount = useRecordContext();
    const { data: customer, isPending } = useGetOne('customers', { id: record.accountHolder });

    function handleNameChange(e: ChangeEvent<HTMLInputElement>): void {
        setName(e.target.value);
    }

    function handleQuantityChange(e: ChangeEvent<HTMLInputElement>): void {
        setQuantity(parseFloat(e.target.value));
    }

    function handleCurrencyChange(e: ChangeEvent<HTMLSelectElement>): void {
        setCurrency(e.target.value);
    }

    function handlePriceChange(e: ChangeEvent<HTMLInputElement>): void {
        setPrice(parseFloat(e.target.value));
    }

    function handleCheckoutPanelToggle(value: boolean): void {
        try {
            if (name.length <= 0) {
                throw new Error("Reason is required");
            }
            if (price <= 0) {
                throw new Error("Price must be greater than 0");
            }
            setToggleCheckoutPanel(value);
        } catch (error: any) {
            notify(error.message, { type: 'error' })
        }
    }

    function resetForm(): void {
        setName('');
        setQuantity(0);
        setCurrency('gbp');
        setPrice(0);
        handleCheckoutPanelToggle(false);
    }

    function cleanHtmlContent(): string {
        let invoiceTemplateString: string = settingValue;
        const today: Date = new Date();
        const todayString: string = today.getDate() + ' ' + findMonth(today.getMonth() + 1) + ' ' + today.getFullYear();
        if (invoiceTemplateString.includes('{{invoice_data_table}}')) {
            const htmlString: string = `
                <table cellpadding="0" cellspacing="0" style="margin-left:auto; font-size:12px;">
                    <tbody><tr><td>Invoice #</td><td style="padding-left:10px;"><strong>kgjknt5bdkjsmwI</strong></td></tr>
                        <tr><td>Date</td><td style="padding-left:10px;">${ todayString }</td></tr>
                        <tr><td>Due</td><td style="padding-left:10px;">${ todayString }</td></tr>
                        <tr><td>Currency</td><td style="padding-left:10px;">${ currency.toUpperCase() + ' (' + convertCurrencyToSymbol(currency) + ')' }</td></tr>
                    </tbody>
                </table>
            `;
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{invoice_data_table}}', htmlString);
        }
        if (invoiceTemplateString.includes('{{invoice_status}}')) {

            invoiceTemplateString = invoiceTemplateString.replaceAll('{{invoice_status}}', "<div><span class=\"badge\">Due</span></div>" );
        }
        if (invoiceTemplateString.includes('{{invoice_id}}')) {
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{invoice_id}}', 'kgjknt5bdkjsmwI');
        }
        if (invoiceTemplateString.includes('{{customer_billing_address}}')) {

            let addressString: string = `
                <address style="width:100%;">
                    Joe Bloggs</br>
                    123 Street </br>
                    In A City </br>
                    Some County </br>
                    AA1 1AA</br>
                </address>
            `;
            const billingAddresses: any = record.billingAddress;
            if (billingAddresses !== undefined && billingAddresses[0] !== undefined) {
                const billingAddress: any = billingAddresses[0];
                let lineTwo: string = '';
                if (billingAddress['lineTwo'] !== undefined && billingAddress['lineTwo'] !== null) {
                    lineTwo = billingAddress['lineTwo'];
                }
                let county: string = '';
                if (billingAddress['county'] !== undefined && billingAddress['county'] !== null) {
                    county = billingAddress['county'];
                }
                addressString = `
                    <address style="width:100%;">
                        ${ customer?.name ?? '' }</br>
                        ${ billingAddress['lineOne'] ?? '' }</br>
                        ${ lineTwo }
                        ${ billingAddress['city'] ?? '' }</br>
                        ${ county }
                        ${ billingAddress['postcode'] ?? '' }</br>
                        ${ billingAddress['country'] ?? '' }</br>
                    </address>
                `;
            }

            invoiceTemplateString = invoiceTemplateString.replaceAll('{{customer_billing_address}}', addressString );
        }
        if (invoiceTemplateString.includes('{{items_table}}')) {
            let htmlString: string = `
                <table class="items">
                    <thead>
                      <tr>
                        <th style="width:50%;">Line Item</th>
                        <th style="width:15%;">Price</th>
                        <th style="width:15%;">Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr class="avoid-break">
                        <td>
                          ${ (name !== '' ? name : 'Item 1') }
                        </td>
                        <td class="nowrap">${ ( convertCurrencyToSymbol(currency) + price.toFixed(2) ) }</td>
                        <td class="nowrap">${ quantity }</td>
                      </tr>
                    </tbody>
                </table>
            `;

            invoiceTemplateString = invoiceTemplateString.replaceAll('{{items_table}}', htmlString );
        }
        if (invoiceTemplateString.includes('{{totals_data_table}}')) {
            let priceWithTax: number = price + (price * 0.2);
            let taxString: string = '';
            if (currency === 'gbp') {
                taxString = `<tr class="line"><td>Tax (VAT 20%)</td><td class="text-right">${ ( convertCurrencyToSymbol(currency) + priceWithTax.toFixed(2) ) }</td></tr>`;
            }
            const htmlString: string = `
                <table style="width:100%; border-collapse:collapse;">
                    <tbody><tr class="line"><td>Subtotal</td><td class="text-right">${ ( convertCurrencyToSymbol(currency) + price.toFixed(2) ) }</td></tr>
                      ${ taxString }
                      <tr class="grand"><td><strong>Total Due</strong></td><td class="text-right"><strong>${ ( convertCurrencyToSymbol(currency) + priceWithTax.toFixed(2) ) }</strong></td></tr>
                    </tbody>
                </table>
            `;
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{totals_data_table}}', htmlString);
        }
        if (invoiceTemplateString.includes('{{support_email}}')) {
            const phoneString: string = `support@noknok.ai`
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{support_email}}', phoneString );
        }
        if (invoiceTemplateString?.includes('{{logo_source}}')) {
            invoiceTemplateString = invoiceTemplateString.replaceAll('{{logo_source}}', '/images/icon/nn-logo.png');
        }
        return invoiceTemplateString;
    }

    return (
        <div className="single-charge-container">
            <div className="preview__panel">
                <div className="panel__panel">
                    { (loadingSetting) &&
                        <div className="panel__preview-loader">
                            <span>{ "Loading Invoice Preview" }</span>
                            <CircularProgress size={ 40 } />
                        </div>
                    }
                    { (!loadingSetting) &&
                        <div className="panel__preview">
                            <span className="preview__title" >{ "Invoice Preview" }</span>
                            <div className="preview__body">
                                <div className="body__plain"
                                     dangerouslySetInnerHTML={ {
                                         __html: cleanHtmlContent()
                                     } } />
                            </div>
                        </div>
                    }
                </div>
            </div>
            <div className="charge__form">
                <span className="form__title">{ "Create a one off charge to the customer" }</span>
                <div className="form__form">
                    <div className="form__item">
                        <span className="form__label">{ "Charge Reason" }</span>
                        <input name="name" type="text" value={ name } onChange={ handleNameChange } />
                        <span className="form__helper">{ "* This value appears as the invoice line item" }</span>
                    </div>
                    <div className="form__item">
                        <span className="form__label">{ "Quantity" }</span>
                        <input type="number" name="quantity" value={ quantity } onChange={ handleQuantityChange } />
                    </div>
                    <div className="form__item">
                        <span className="form__label">{ "Price" }</span>
                        <input type="number" name="price" step={ 0.1 } value={ price } onChange={ handlePriceChange } />
                    </div>
                    <div className="form__item">
                        <span className="form__label">{ "Currency" }</span>
                        <select value={ currency } name="currency" onChange={ handleCurrencyChange } >
                            { currencies.map( (item: any, index: number): JSX.Element => {
                                return (
                                    <option value={ item.id } key={ index }>{ item.name }</option>
                                );
                            } ) }
                        </select>
                    </div>
                    <div className="form__actions">
                        <SingleChargeCheckoutDrawer payload={ {
                            name: name,
                            quantity: quantity,
                            currency: currency,
                            price: price,
                        } }
                                                    toggleCheckoutPanel={ toggleCheckoutPanel }
                                                    handleCheckoutPanelToggle={ handleCheckoutPanelToggle }
                                                    resetForm={ resetForm }
                        />
                    </div>
                    <span className="form__helper right">{ "* A new single charge invoice will be created" }</span>
                </div>

            </div>
        </div>
    );
}